import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicInfoLayout from '../components/public/PublicInfoLayout';

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Service | Keller Williams Southern Africa';
  }, []);

  return (
    <PublicInfoLayout>
      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-10 shadow-sm space-y-8">
        {/* Header */}
        <div className="border-b border-slate-100 pb-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            Terms &amp; Policies
          </div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Terms of Service
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Mega Agent Productivity Platform (MAPP) &bull; Keller Williams Southern Africa
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Effective Date: October 2026
          </p>
        </div>

        {/* Section 1: Authorised KWSA Users */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            1. Authorised KWSA Users
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            The Mega Agent Productivity Platform (&ldquo;MAPP&rdquo;) is a proprietary business system provided exclusively
            for authorized Keller Williams Southern Africa (&ldquo;KWSA&rdquo;) real estate associates, Market Centre leadership,
            staff, and regional administrators. Access is granted solely to individuals with approved, active credentials.
          </p>
        </section>

        {/* Section 2: Business Use of MAPP */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            2. Business Use of MAPP
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            MAPP is designed and operated exclusively for legitimate real estate operations, including property listing
            management, mandate syndication, Comparative Market Analysis (CMA), sales and rental pipeline tracking, transaction
            auditing, training resource access, and client communications. Use of MAPP for non-KWSA commercial activities, personal
            ventures, or any unauthorized purpose is strictly prohibited.
          </p>
        </section>

        {/* Section 3: Account Responsibility */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            3. Account Responsibility
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Each user is personally responsible for maintaining the confidentiality and security of their login credentials and
            any connected external accounts. Users must never share passwords or authentication tokens with third parties. Any activity
            or transaction processed under an associate&apos;s account is presumed to be authorized by that user. Suspected credential
            loss, compromise, or unauthorized access must be reported immediately to KWSA support.
          </p>
        </section>

        {/* Section 4: Acceptable Use */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            4. Acceptable Use
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Users agree to operate MAPP in full compliance with applicable South African laws, the Estate Agency Affairs Act /
            Property Practitioners Act, regulatory codes of conduct, and KWSA organizational standards. Users must provide truthful,
            accurate, and non-misleading information regarding listings, transaction terms, pricing, and client records.
          </p>
        </section>

        {/* Section 5: Confidentiality */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            5. Confidentiality
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            All information available within MAPP—including Market Centre production figures, associate capping progressions, commission
            splits, client contact details, transaction documents, and internal operational metrics—constitutes confidential business
            information of KWSA. Users must maintain strict confidentiality and may not disclose or disseminate proprietary MAPP data
            outside authorized real estate brokerage functions.
          </p>
        </section>

        {/* Section 6: Client and Lead Data */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            6. Client and Lead Data
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Client details, seller information, landlord records, and portal leads maintained in MAPP are proprietary to the brokerage
            and assigned associates. Users must process client and prospective buyer data lawfully, respectfully, and exclusively for
            the real estate services requested by the consumer. Downloading, exporting, or extracting client or lead lists for
            unauthorized external use or competing ventures is strictly forbidden.
          </p>
        </section>

        {/* Section 7: Email Communications */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            7. Email Communications
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            MAPP features tools enabling associates to generate and transmit Seller Updates, Landlord Updates, and listing reports.
            All communications dispatched through MAPP must:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li>Be sent only to recipients who have an active listing, mandate, or bona fide transaction relationship with the associate or Market Centre.</li>
            <li>Accurately reflect property activity, portal views, inquiries, and market conditions without exaggeration or fabrication.</li>
            <li>Reflect professional standards and adhere to anti-spam requirements. Dissemination of unsolicited bulk marketing or spam via MAPP tools is strictly prohibited.</li>
          </ul>
        </section>

        {/* Section 8: Google Workspace Connection */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            8. Google Workspace Connection
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            MAPP allows authorized associates to connect an approved Google Workspace account ending in <code className="text-red-700 font-mono">@kwsa.co.za</code> or <code className="text-red-700 font-mono">@kwsouthafrica.co.za</code> to send update emails directly from their corporate mailbox.
          </p>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Users understand that this integration requests only the permissions necessary to identify the connected account and dispatch outbound messages initiated by the user. MAPP does not read, search, or download inbox messages. Associates may disconnect this integration at any time within their MAPP settings, which terminates token validity and removes stored credentials immediately.
          </p>
        </section>

        {/* Section 9: Prohibited Misuse */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            9. Prohibited Misuse
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Users shall not, directly or indirectly:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li>Attempt to gain unauthorized access to data, accounts, or Market Centre scopes other than those assigned to them.</li>
            <li>Introduce malware, viruses, automated bots, scrapers, or other malicious code into the platform.</li>
            <li>Decompile, reverse engineer, disassemble, or copy any software, layout, or proprietary algorithm used in MAPP.</li>
            <li>Circumvent or disable any security features, authentication controls, or rate limits enforced by MAPP or its underlying APIs.</li>
            <li>Transmit abusive, defamatory, harassing, or unlawful content through MAPP communication channels.</li>
          </ul>
        </section>

        {/* Section 10: Service Availability */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            10. Service Availability
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            KWSA strives to provide continuous, high-availability service for MAPP. However, MAPP is provided on an &ldquo;as-is&rdquo;
            and &ldquo;as-available&rdquo; operational basis. KWSA does not warrant that the platform will operate completely uninterrupted,
            bug-free, or without temporary downtime for routine maintenance, emergency patching, or external provider outages.
          </p>
        </section>

        {/* Section 11: Changes to MAPP */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            11. Changes to MAPP
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            KWSA reserves the right to modify, upgrade, redesign, or deprecate any feature, report, or integration within MAPP at its sole
            operational discretion to improve platform efficiency, ensure compliance, or adapt to evolving real estate technology standards.
          </p>
        </section>

        {/* Section 12: Termination and Restriction of Access */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            12. Termination and Restriction of Access
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Access to MAPP is a privilege conditioned on active association with KWSA. KWSA reserves the right to immediately suspend,
            restrict, or revoke access to MAPP without prior notice if:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li>An associate leaves or is de-registered from Keller Williams Southern Africa or an affiliated Market Centre.</li>
            <li>A user violates these Terms of Service, KWSA operational policies, or applicable law.</li>
            <li>A security breach, account compromise, or malicious activity is detected originating from the account.</li>
          </ul>
        </section>

        {/* Section 13: Support and Contact Information */}
        <section className="space-y-3 border-t border-slate-100 pt-6">
          <h2 className="text-xl font-bold text-slate-900">
            13. Support and Contact Information
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            For assistance with MAPP access, reporting technical difficulties, or inquiries regarding these Terms of Service, please contact KWSA Support:
          </p>
          <div className="rounded-lg bg-slate-50 p-4 border border-slate-200 text-sm text-slate-700">
            <p><strong>Platform:</strong> Mega Agent Productivity Platform (MAPP)</p>
            <p><strong>Operating Entity:</strong> Keller Williams Southern Africa</p>
            <p><strong>Support Email:</strong> <a href="mailto:support@kwsa.co.za" className="text-red-700 hover:underline">support@kwsa.co.za</a></p>
            <p><strong>General Inquiries:</strong> <a href="mailto:info@kwsa.co.za" className="text-red-700 hover:underline">info@kwsa.co.za</a></p>
          </div>
        </section>

        {/* Footer Navigation within Page */}
        <div className="border-t border-slate-200 pt-6 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-4">
            <Link to="/about" className="font-medium text-slate-600 hover:text-red-700 transition-colors">
              &larr; About MAPP
            </Link>
            <span className="text-slate-300">|</span>
            <Link to="/privacy" className="font-medium text-slate-600 hover:text-red-700 transition-colors">
              Privacy Policy &rarr;
            </Link>
          </div>
          <Link to="/login" className="font-medium text-red-700 hover:underline">
            Sign In to MAPP
          </Link>
        </div>
      </div>
    </PublicInfoLayout>
  );
}
